import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const TermsSection = ({ title, description }) => (
  <Box>
    <Typography
      variant="h6"
      gutterBottom
      sx={{
        fontWeight: 'bold',
      }}
    >
      {title}
    </Typography>
    {description}
  </Box>
);

TermsSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.object.isRequired,
};

const Paragraph = ({ children }) => (
  <Typography component="p" color="textSecondary" sx={{ textAlign: 'justify', mb: 1.5 }}>
    {children}
  </Typography>
);

Paragraph.propTypes = {
  children: PropTypes.node.isRequired,
};

const List = ({ children }) => (
  <Typography
    component="ul"
    color="textSecondary"
    sx={{ textAlign: 'justify', mb: 1.5, pl: 3, '& li': { mb: 1 } }}
  >
    {children}
  </Typography>
);

List.propTypes = {
  children: PropTypes.node.isRequired,
};

const TermsContent = () => {
  const termsData = [
    {
      title: '1. SCOPE OF APPLICATION',
      description: (
        <Box>
          <Paragraph>
            <b>1.1</b> These Terms and Conditions apply to the legal relationship between (i)
            Cartify Portal FZCO and any of its Licensees (together the <b>“Provider”</b>) and (ii)
            each Customer that executes a Services Agreement which references or is accompanied by
            this document.
          </Paragraph>
          <Paragraph>
            <b>1.2</b> The relationship between the Customer and the Provider is governed by the
            Agreement. The relationship between any two Customers of the Mobile Application is a
            matter solely between those Customers, and the Provider is not a party to it.
          </Paragraph>
          <Paragraph>
            <b>1.3</b> Cartify Portal FZCO and its Licensees shall under no circumstances be deemed
            jointly or severally liable towards any Customer, Authorized Customer Entity or
            Authorized User on any grounds.
          </Paragraph>
          <Paragraph>
            <b>1.4</b> By subscribing to and using the Services, the Customer irrevocably agrees to
            be bound by and to comply with the Agreement, and to procure that its Authorized Users
            and Authorized Customer Entities comply with it.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '2. DEFINITIONS',
      description: (
        <Box>
          <Paragraph>As used in these Terms and Conditions:</Paragraph>
          <Paragraph>
            <b>“Agreement”</b> means the agreement between the Customer and the Provider, consisting
            of these Terms and Conditions, the Privacy Policy, any Services Agreement executed
            between the Customer and the Provider, and any Annex or Statement of Work entered into
            under it.
          </Paragraph>
          <Paragraph>
            <b>“Applicable Laws”</b> means all laws, statutes, regulations, rules, judgments, orders
            and other requirements of any governmental, judicial or regulatory authority applicable
            to a Party in relation to its activities under the Agreement.
          </Paragraph>
          <Paragraph>
            <b>“Authorized Customer Entities”</b> means the specific Customer-affiliated entities
            named in a Services Agreement which are authorized to access and use the Services during
            the Subscription Term.
          </Paragraph>
          <Paragraph>
            <b>“Authorized User(s)”</b> means the end users of a Customer or of its Authorized
            Customer Entities who receive a user ID or other access credentials to access and use
            the Services. The Services are available only to legal entities and to persons who are
            at least eighteen (18) years old and capable of forming legally binding contracts.
          </Paragraph>
          <Paragraph>
            <b>“Commission Fees”</b> means the fees payable by a Supplier to the Provider in respect
            of transactions concluded through the Mobile Application, as set forth in the relevant
            Services Agreement.
          </Paragraph>
          <Paragraph>
            <b>“Confidential Information”</b> means all non-public information disclosed by one
            Party to the other, whether or not marked confidential, that by its nature or the
            circumstances of its disclosure ought reasonably to be understood as confidential,
            including information relating to the SaaS, the Mobile Application, its source code and
            concept, and the business of either Party.
          </Paragraph>
          <Paragraph>
            <b>“Customer”</b> means either an Operator or a Supplier, as the case may be, that uses
            the Services and is bound by the Agreement. Where a provision of these Terms and
            Conditions is expressed to apply to Operators only or to Suppliers only, it applies
            solely to that category of Customer.
          </Paragraph>
          <Paragraph>
            <b>“Customer Data”</b> means any data owned by a Customer or an Authorized Customer
            Entity that is submitted to the Services for processing, transmission, storage or use.
          </Paragraph>
          <Paragraph>
            <b>“Fresh Funds”</b> means United States Dollars in cash, or funds transferred and
            freely available and transferable through the banking system, in each case not subject
            to any restriction on withdrawal or transfer. For the avoidance of doubt, Fresh Funds
            excludes any balance subject to banking restrictions.
          </Paragraph>
          <Paragraph>
            <b>“Intellectual Property Rights”</b> means all rights in copyrights, patents,
            trademarks, trade names, trade secrets, source code, designs, databases and other
            intellectual or industrial property, whether registered or not, and all applications for
            any of them.
          </Paragraph>
          <Paragraph>
            <b>“Licensee(s)”</b> means any organization, company or individual granted by Cartify
            Portal FZCO the right to operate the Mobile Application in a relevant territory.
          </Paragraph>
          <Paragraph>
            <b>“Losses”</b> means all reasonable attorneys’ fees, costs of investigation, discovery,
            litigation and settlement, and any resulting liabilities, claims, damages, settlements,
            judgments and awards, including associated taxes, interest and penalties, but excluding
            indirect and consequential damages.
          </Paragraph>
          <Paragraph>
            <b>“Mobile Application”</b> means the proprietary client software made available by the
            Provider under the trade name <b>“CARTIFY PORTAL,”</b> including any updates,
            enhancements or modifications. All Intellectual Property Rights in the Mobile
            Application are owned by or licensed to the Provider.
          </Paragraph>
          <Paragraph>
            <b>“Operators”</b> means the restaurants and other operators that subscribe to the
            Services as buyers of Products.
          </Paragraph>
          <Paragraph>
            <b>“Products”</b> means the food and beverage and related products supplied by Suppliers
            to Operators through the Mobile Application.
          </Paragraph>
          <Paragraph>
            <b>“Provider”</b> has the meaning given in Article 1.1.
          </Paragraph>
          <Paragraph>
            <b>“Purchase Order(s)”</b> means an order placed through the Mobile Application by an
            Operator to a Supplier for Products.
          </Paragraph>
          <Paragraph>
            <b>“SaaS”</b> means the Provider’s proprietary software as a service platform and
            related services, including the Mobile Application, any website, related documentation
            and any Updates.
          </Paragraph>
          <Paragraph>
            <b>“Services”</b> means collectively, the SaaS, the Support Services and any
            professional services provided by the Provider under the Agreement. The core function of
            the Services is to facilitate the placement of Purchase Orders by Operators to
            Suppliers.
          </Paragraph>
          <Paragraph>
            <b>“Services Agreement”</b> means an agreement form issued by the Provider and executed
            between a Customer and the Provider, setting out the commercial terms applicable to that
            Customer, including fees, rate, term and any service commitments. The Services Agreement
            is subject to these Terms and Conditions, and in case of contradiction the Services
            Agreement prevails.
          </Paragraph>
          <Paragraph>
            <b>“Subscription Fees”</b> means the fees payable by an Operator to the Provider for the
            Services, as set forth in the relevant Services Agreement.
          </Paragraph>
          <Paragraph>
            <b>“Subscription Term”</b> means the period during which a Customer is permitted to
            access and use the SaaS, as set forth in the applicable Services Agreement.
          </Paragraph>
          <Paragraph>
            <b>“Suppliers”</b> means the food and beverage suppliers that use the Services to
            display, offer and sell Products.
          </Paragraph>
          <Paragraph>
            <b>“Updates”</b> means improvements, enhancements or modifications to the SaaS provided
            by the Provider at no charge.
          </Paragraph>
          <Paragraph>
            <b>“User Content”</b> means any content submitted, posted or displayed by Authorized
            Users through the Mobile Application, including Product listings, prices and catalogue
            data.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '3. THE PROVIDER’S ROLE AND THE LIABILITY BOUNDARY',
      description: (
        <Box>
          <Paragraph>
            <b>3.1</b> The Mobile Application is a marketplace that facilitates the placement of
            Purchase Orders by Operators to Suppliers. The Provider provides the technology platform
            only.
          </Paragraph>
          <Paragraph>
            <b>3.2</b> Every contract for the sale of Products formed through the Mobile Application
            is concluded directly and exclusively between the relevant Operator and the relevant
            Supplier.{' '}
            <b>
              The Provider is not a party to any such contract, and does not act as buyer, seller,
              agent, broker, distributor, guarantor or insurer in respect of it.
            </b>
          </Paragraph>
          <Paragraph>
            <b>3.3</b> The Supplier is solely responsible for the delivery of Products and for their
            quality, safety, conformity, labelling and regulatory compliance. The Operator is solely
            responsible for payment of the purchase price. Delivery and payment take place directly
            between the Supplier and the Operator, without any involvement or liability of the
            Provider.
          </Paragraph>
          <Paragraph>
            <b>3.4</b> The Provider does not approve, endorse, vet, verify, curate, recommend or
            warrant any Supplier, any Operator, or any Product. Any listing, categorisation or
            display of a Product on the Mobile Application is a technical and catalogue function
            only and does not constitute any representation by the Provider as to the Product or the
            Supplier.
          </Paragraph>
          <Paragraph>
            <b>3.5</b> The Provider may review Product listings for compliance with the Mobile
            Application’s catalogue standards, including data completeness, unit and packaging
            format, barcode presence and category placement. Such review is limited to catalogue and
            technical compliance and does not extend to the quality, safety or fitness of any
            Product.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '4. LIMITED ACCESS AND USE',
      description: (
        <Box>
          <Paragraph>
            <b>4.1 Access grant.</b> Subject to the Customer’s continuing compliance with the
            Agreement and timely payment of the applicable fees, the Provider grants to the Customer
            a limited, personal, non-exclusive, non-transferable and non-sublicensable right for its
            Authorized Users and Authorized Customer Entities to access and use the features of the
            SaaS during the Subscription Term, solely through the Mobile Application and solely for
            the purposes set out in the Agreement.
          </Paragraph>
          <Paragraph>
            <b>4.2 Access protocols.</b> The Provider shall provide the Customer with the access
            credentials necessary for its Authorized Users to access the SaaS. The Customer is
            responsible for all acts, omissions and breaches of its Authorized Users and Authorized
            Customer Entities, and shall make them aware of and procure their compliance with the
            provisions of the Agreement applicable to their use of the SaaS.
          </Paragraph>
          <Paragraph>
            The Customer shall promptly notify the Provider upon becoming aware of any unauthorised
            access to, loss, theft, disclosure or compromise of any account credentials or any
            suspected security incident affecting its account or use of the SaaS.
          </Paragraph>
          <Paragraph>
            <b>4.3 System requirements.</b> The Customer is solely responsible for obtaining and
            maintaining the hardware, software and connectivity required to use the Mobile
            Application. The Provider is not responsible for problems arising from the Customer’s
            network connections or from non-compliance with system requirements.
          </Paragraph>
          <Paragraph>
            <b>4.4 Account administration.</b> The Customer shall designate at least one Authorized
            User to act as administrator and principal point of contact with the Provider.
          </Paragraph>
          <Paragraph>
            <b>4.5 User Content.</b> The Customer is solely responsible for its User Content,
            including its Product listings, prices and catalogue data, and for ensuring that such
            content is accurate, current and lawful. The Provider does not endorse, verify or
            guarantee the accuracy of any User Content and has no obligation to review, filter or
            remove it, save as set out in Article 4.6. The Customer retains ownership of its User
            Content and grants the Provider a non-exclusive, worldwide, royalty-free licence to
            host, copy, process, transmit and display it for the purpose of providing and improving
            the Services. The Provider owns all aggregated, anonymised, statistical, analytical and
            usage data generated through the SaaS. Such data does not identify the Customer or any
            individual and may be used by the Provider for analytics, research, service improvement
            and commercial purposes.
          </Paragraph>
          <Paragraph>
            <b>4.6 Objectionable content.</b> If the Provider reasonably believes any User Content
            to be unlawful, inaccurate or otherwise objectionable, it will notify the Customer, and
            the Customer shall promptly remove or correct it. If the Customer fails to do so within
            ten (10) days, the Provider may disable access to that content.
          </Paragraph>
          <Paragraph>
            <b>4.7 Confidentiality of commercial data.</b> The Provider does not share a Customer’s
            Product lists or prices with other Customers except as necessary to operate the
            marketplace, namely by displaying a Supplier’s Product listings and prices to Operators
            as potential buyers.
          </Paragraph>
          <Paragraph>
            <b>4.8 Advertising and promoted placement.</b>
          </Paragraph>
          <Paragraph>
            <b>4.8.1</b> The Provider may display advertising, sponsored listings, promoted
            placements and third-party offers through the Mobile Application as part of the
            Services. The Provider may determine their format, placement, ranking, prominence and
            duration.
          </Paragraph>
          <Paragraph>
            <b>4.8.2</b> Subject to Applicable Laws and the Privacy Policy, the Provider may use a
            Customer’s activity on the Mobile Application, including browsing, search and order
            history, to select, target, measure and improve advertising and promotional content
            shown to that Customer. The Provider shall not disclose a Customer’s Confidential
            Information to advertisers for their independent use.
          </Paragraph>
          <Paragraph>
            <b>4.8.3</b> A Customer wishing to advertise, sponsor, promote or obtain enhanced
            placement for its Products or other content through the Mobile Application may request
            such services from the Provider. Any such advertising services shall be subject to the
            Provider’s prior approval and a separate advertising order, insertion order, Statement
            of Work or Services Agreement setting out the applicable fees and commercial terms.
          </Paragraph>
          <Paragraph>
            <b>4.8.4</b> The Provider may reject, modify, suspend or remove any advertisement or
            promotional content that it reasonably considers unlawful, misleading, inaccurate,
            infringing, inappropriate or otherwise likely to expose the Provider or the Mobile
            Application to legal, regulatory or reputational risk.
          </Paragraph>
          <Paragraph>
            <b>4.8.5</b> The Customer shall be solely responsible for the legality, accuracy and
            compliance of any advertising content submitted by it and for the Products or services
            advertised. The Provider does not endorse or guarantee any advertised Product, service
            or offer and does not guarantee any minimum number of views, clicks, orders, sales or
            other commercial results.
          </Paragraph>
          <Paragraph>
            <b>4.8.6</b> Nothing in this Article excludes or limits any right or obligation that
            cannot lawfully be excluded or limited under Applicable Laws.
          </Paragraph>
          <Paragraph>
            <b>4.9 Restrictions.</b> The Customer shall not, and shall procure that its Authorized
            Users and Authorized Customer Entities shall not:
          </Paragraph>
          <List>
            <li>(a) use the SaaS in any manner inconsistent with or in breach of the Agreement;</li>
            <li>
              (b) develop, operate, finance or provide material assistance to a business-to-business
              marketplace platform that competes with the Mobile Application in the relevant
              territory, during the Subscription Term and for twelve (12) months following the
              expiry or termination of the Agreement;
            </li>
            <li>
              (c) decompile, reverse engineer (except to the extent permitted by law for
              interoperability) or otherwise attempt to derive the source code of the SaaS, or
              extract any trade secret from it;
            </li>
            <li>
              (d) use the SaaS to operate the business of, or process data on behalf of, a third
              party, or act as a service bureau;
            </li>
            <li>
              (e) resell, sublicense, lease, assign or otherwise economically exploit the SaaS or
              the rights granted under the Agreement; or
            </li>
            <li>
              (f) circumvent the Provider in the manner and to the extent addressed in Article 8.6.
            </li>
          </List>
          <Paragraph>
            For the avoidance of doubt, nothing in this Article restricts a Supplier from selling,
            marketing or distributing its products through any other channel, platform or method,
            and nothing in this Article requires any Customer to deal exclusively with or through
            the Mobile Application.
          </Paragraph>
          <Paragraph>
            <b>4.10 No interference and marketplace integrity.</b> The Customer shall not take any
            action designed or intended to interfere with the proper working or security of the
            SaaS, to circumvent its access controls, or to impose an unreasonable load on its
            infrastructure. The Customer shall not use any robot, spider, scraper or other automated
            tool to access or copy the SaaS without the Provider’s prior written consent. The
            Customer shall not manipulate prices, submit fake orders or reviews, create multiple
            accounts to gain an unfair advantage, or engage in any other activity that disrupts the
            integrity of the Mobile Application.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '5. SUPPORT SERVICES AND PLATFORM OPERATION',
      description: (
        <Box>
          <Paragraph>
            <b>
              5.1 Technical support. At no additional charge and during the Provider’s normal
              business hours, being 09:00 to 17:00 local time, Monday to Friday, excluding public
              holidays, the Provider will provide reasonable technical support for Authorized User
              requests by telephone at{' '}
              <a href="tel:+96171429921" target="_blank" rel="noreferrer">
                +961 71 429 921
              </a>{' '}
              or by email to{' '}
              <a href="mailto:support@cartify.org" target="_blank" rel="noreferrer">
                support@cartify.org
              </a>
              .
            </b>
          </Paragraph>
          <Paragraph>
            <b>5.2 Updates.</b> The Customer will be given access to Updates implemented during the
            Subscription Term. The Provider may in future offer optional value-added features for a
            separate fee.
          </Paragraph>
          <Paragraph>
            <b>5.3 Scheduled maintenance.</b> The Provider may take down the servers hosting the
            SaaS to conduct scheduled and emergency maintenance. It will use commercially reasonable
            efforts to perform scheduled maintenance outside regular business hours and will give at
            least twenty-four (24) hours’ notice for non-emergency maintenance. The Provider is not
            responsible for losses arising from unavailability of the SaaS during maintenance.
          </Paragraph>
          <Paragraph>
            <b>5.4 Platform availability.</b> The SaaS is provided on an <b>“as available”</b>{' '}
            basis. The Provider does not guarantee uninterrupted, error-free or continuous
            availability of the SaaS and shall not be liable for any interruption, delay,
            maintenance or technical failure.
          </Paragraph>
          <Paragraph>
            <b>5.5 Suspension.</b> The Provider may suspend or restrict any account or access to the
            SaaS, with or without prior notice, where it reasonably believes that a Customer or
            Authorized User has breached the Agreement, engaged in fraud or illegal activity,
            created a security risk, or failed to pay any amount due, or where such action is
            necessary to protect the SaaS, its users or the Provider’s business. The Provider shall
            not be liable for any losses arising from such suspension.
          </Paragraph>
          <Paragraph>
            <b>5.6 Fraud protection.</b> The Provider reserves the right to investigate any
            suspected fraudulent, abusive or unlawful activity. During any investigation, the
            Provider may suspend accounts, delay transactions, withhold amounts due, remove
            listings, and cooperate with competent authorities where required by law.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '6. FEES, CURRENCY AND PAYMENT',
      description: (
        <Box>
          <Paragraph>
            <b>6.1 Fees.</b> The fees for the Services, being the Subscription Fees payable by
            Operators and the Commission Fees payable by Suppliers, are set out in the applicable
            Services Agreement and are payable in accordance with its terms.
          </Paragraph>
          <Paragraph>
            <b>6.2 Currency of account.</b> All fees under the Agreement are denominated and payable
            in United States Dollars in Fresh Funds. Where an underlying transaction between an
            Operator and a Supplier is denominated in Lebanese Pounds, the value of that transaction
            shall be converted to United States Dollars, for the purpose of calculating any
            Commission Fee, at the reference rate and on the date specified in the applicable
            Services Agreement.
          </Paragraph>
          <Paragraph>
            <b>6.3 Late payment.</b> Any amount not paid when due shall bear interest at one percent
            (1%) per month from the due date until actual payment, and the Provider may suspend the
            relevant Customer’s access to the Services if payment remains outstanding thirty (30)
            days after the due date, in each case without prejudice to the Provider’s other rights.
          </Paragraph>
          <Paragraph>
            <b>6.4 Taxes.</b> All fees are exclusive of taxes. Each Customer is solely responsible
            for all sales, use and value added taxes associated with its use of the Services. The
            Provider shall issue a tax-compliant invoice in respect of the fees it charges.
          </Paragraph>
          <Paragraph>
            <b>6.5 Non-refundable.</b> Except as expressly stated in the Agreement, fees that are
            due and paid in respect of Services rendered are non-cancellable and non-refundable.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '7. ALLOCATION OF RISK AND LIABILITY',
      description: (
        <Box>
          <Paragraph>
            <b>7.1 Mutual representations.</b> Each Party represents and warrants that it has the
            authority to enter into the Agreement, that its performance does not conflict with any
            Applicable Law or other agreement binding on it, and that the Agreement constitutes a
            valid and binding obligation enforceable against it.
          </Paragraph>
          <Paragraph>
            <b>7.2 Supplier representations.</b> Each Supplier further represents and warrants, on a
            continuing basis, that:
          </Paragraph>
          <List>
            <li>
              (a) it holds all licences, permits, registrations and approvals required under
              Applicable Laws to produce, store, handle, market and supply its Products, including
              all applicable health and food safety authorisations; and
            </li>
            <li>
              (b) its Products comply with all Applicable Laws as to quality, safety, labelling and
              packaging.
            </li>
          </List>
          <Paragraph>
            The Provider may at any time require a Supplier to provide evidence of compliance with
            this Article, and the Supplier shall provide it promptly. This right does not oblige the
            Provider to verify any Supplier’s compliance, and the Provider assumes no liability by
            exercising or not exercising it.
          </Paragraph>
          <Paragraph>
            <b>7.3 Compliance.</b> Each Customer represents and warrants that its activities comply
            with all Applicable Laws, including those relating to taxation, anti-money laundering,
            anti-bribery, consumer protection and, where applicable, food safety.
          </Paragraph>
          <Paragraph>
            <b>7.4 Provider warranty.</b> The Provider shall use commercially reasonable efforts to
            perform the Services in a professional manner consistent with generally accepted
            industry practices. Except as expressly provided in the Agreement, the Provider does not
            warrant that the Services will be uninterrupted, error-free or suitable for any
            particular purpose.
          </Paragraph>
          <Paragraph>
            <b>7.5 Third-party services.</b> The Provider is not responsible for any services, acts,
            omissions, delays, interruptions or failures of third parties used in connection with or
            necessary for the operation of the SaaS, including payment processors, banks, financial
            institutions, delivery providers, telecommunications providers, internet service
            providers, cloud or hosting providers, application stores and other technology or
            infrastructure providers, except to the extent that such liability cannot be excluded or
            limited under Applicable Law.
          </Paragraph>
          <Paragraph>
            <b>7.6 Disclaimer.</b> Except as expressly stated in the Agreement, and to the maximum
            extent permitted by Applicable Law, the Provider disclaims all other warranties,
            express, implied or statutory, including warranties of merchantability, fitness for a
            particular purpose, accuracy, title, non-infringement and uninterrupted or error-free
            operation. The Provider does not warrant that the SaaS will meet the Customer’s
            requirements or that any Purchase Order placed through the Mobile Application will be
            performed by the relevant parties. The Customer assumes complete responsibility for its
            selection and use of the SaaS.
          </Paragraph>
          <Paragraph>
            <b>7.7 No liability for transactions.</b> Notwithstanding any other provision, the
            Provider shall not be responsible for, or in connection with, any Purchase Order or sale
            concluded through the SaaS, including any non-performance, defective performance,
            non-payment, non-delivery, delivery of defective Products or non-refund. The Operators
            and the Suppliers are solely responsible under their Purchase Orders, including for
            delivery, payment, product quality and refunds, and the Provider disclaims all liability
            in relation to the relationship between them.
          </Paragraph>
          <Paragraph>
            <b>7.8 Product quality.</b> The Provider does not warrant or guarantee the quality,
            safety or fitness of any Product displayed or marketed through the SaaS. Such warranties
            and all related responsibilities are solely those of the Supplier.
          </Paragraph>
          <Paragraph>
            <b>7.9 Indemnification by the Supplier.</b> Each Supplier shall defend, indemnify and
            hold harmless the Provider from and against all third-party claims, and associated
            Losses, arising out of or relating to (a) that Supplier’s breach of the Agreement, (b)
            its Products, including their quality, safety, labelling, regulatory compliance,
            delivery or non-delivery, and (c) any Purchase Order placed with it through the Mobile
            Application.
          </Paragraph>
          <Paragraph>
            <b>7.10 Indemnification by the Operator.</b> Each Operator shall defend, indemnify and
            hold harmless the Provider from and against all third-party claims, and associated
            Losses, arising out of or relating to (a) that Operator’s breach of the Agreement, and
            (b) its non-payment of, or other default under, any Purchase Order placed by it through
            the Mobile Application.
          </Paragraph>
          <Paragraph>
            <b>7.11 Indemnification for User Content.</b> Each Customer shall defend, indemnify and
            hold harmless the Provider from and against all claims and associated Losses arising out
            of its User Content, including any claim that such content infringes the rights of a
            third party or is inaccurate, unlawful or injurious.
          </Paragraph>
          <Paragraph>
            <b>7.12 Indemnification procedure.</b> The Provider shall give the indemnifying Customer
            prompt written notice of any claim for which it seeks indemnification. The Customer
            shall assume control of the defence and settlement of the claim, provided that it shall
            not settle any claim in a manner that imposes any obligation on the Provider without the
            Provider’s prior written consent. The Provider may participate in the defence at its own
            expense with its own counsel.
          </Paragraph>
          <Paragraph>
            <b>7.13 Electronic records.</b> The Provider’s electronic records, system logs,
            transaction records, communications and database entries shall constitute valid evidence
            of all activities carried out through the SaaS unless proven otherwise by competent
            legal evidence.
          </Paragraph>
          <Paragraph>
            <b>7.14 Limitation of liability.</b> To the maximum extent permitted by Applicable Law,
            neither Party shall be liable under the Agreement for any indirect, incidental,
            consequential, special, exemplary or punitive damages, or for any loss of profits,
            revenue, business, business opportunity, anticipated savings, goodwill, reputation, or
            data, or for any business interruption, regardless of the legal or contractual basis of
            the claim and whether or not such Party has been advised of the possibility of such
            damages.
          </Paragraph>
          <Paragraph>
            Subject to the foregoing and except for liabilities that cannot lawfully be excluded or
            limited, the Provider’s maximum aggregate cumulative liability arising out of or in
            connection with the Agreement, whether in contract, tort, negligence, breach of
            statutory duty or otherwise, shall not exceed fifty percent (50%) of the total fees
            actually paid by the relevant Customer to the Provider under the Agreement during the
            twelve (12) months preceding the event giving rise to the relevant claim.
          </Paragraph>
          <Paragraph>
            Nothing in this Article shall exclude or limit liability to the extent such exclusion or
            limitation is prohibited by Applicable Law.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '8. DURATION AND TERMINATION',
      description: (
        <Box>
          <Paragraph>
            <b>8.1 Duration.</b> The Agreement commences on the start date of the Subscription Term
            and continues until it expires or is terminated in accordance with the Services
            Agreement and these Terms and Conditions.
          </Paragraph>
          <Paragraph>
            <b>8.2 Termination for breach.</b> Either Party may terminate the Agreement if the other
            Party breaches a material provision and fails to remedy the breach within ten (10) days
            following written notice specifying it.
          </Paragraph>
          <Paragraph>
            <b>8.3 Termination for insolvency.</b> Either Party may terminate the Agreement with
            immediate effect if the other Party becomes subject to any proceeding relating to
            bankruptcy, insolvency, receivership, liquidation or assignment for the benefit of
            creditors, or is declared bankrupt.
          </Paragraph>
          <Paragraph>
            <b>8.4 Effects of termination.</b> On expiry or termination, all rights granted under
            the Agreement cease and each Party ceases to use any Confidential Information or
            Intellectual Property Rights of the other. Purchase Orders accepted before termination
            shall be performed in full, and fees accrued in respect of them remain payable.
          </Paragraph>
          <Paragraph>
            <b>8.5 Immediate termination.</b> The Provider may immediately suspend or terminate any
            account, with or without notice, where it reasonably believes that the Customer has
            breached the Agreement, engaged in fraud, illegal activity or misuse of the SaaS,
            engaged in conduct damaging to the Provider’s reputation, or engaged in any activity
            that may expose the Provider or other users to legal, financial or security risk.
          </Paragraph>
          <Paragraph>
            <b>8.6 Non-circumvention.</b> During the Subscription Term, where an Operator places its
            first Purchase Order with a Supplier through the Mobile Application, the Supplier shall
            not supply Products to that Operator otherwise than through the Mobile Application for
            the purpose of avoiding the Commission Fee. This Article does not apply to any Operator
            with which the Supplier had a commercial relationship at any time during the twelve (12)
            months preceding that Operator’s first Purchase Order through the Mobile Application. A
            Supplier may establish that this Article does not apply to a given Operator by producing
            an invoice or equivalent record evidencing a supply to that Operator predating its first
            Purchase Order through the Mobile Application. This Article imposes no obligation on the
            Supplier following the expiry or termination of the Agreement.
          </Paragraph>
          <Paragraph>
            Where a Supplier supplies Products to an Operator in circumstances that constitute
            circumvention of the Provider for the purpose of avoiding the applicable Commission Fee,
            the Supplier shall remain liable for the Commission Fee that would have been payable had
            the relevant transaction been completed through the Mobile Application. The Provider may
            require the Supplier to provide reasonable documentary evidence of transactions relevant
            to the application of this Article and may suspend the Supplier’s access to the Services
            pending resolution of any material dispute concerning such circumvention. This shall be
            without prejudice to any other rights or remedies available to the Provider under the
            Agreement or Applicable Law.
          </Paragraph>
          <Paragraph>
            <b>8.7 Survival.</b> Any provision that by its nature is intended to survive, including
            Articles 6, 7, 9, 10 and 11, survives the expiry or termination of the Agreement.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '9. PROPRIETARY RIGHTS',
      description: (
        <Box>
          <Paragraph>
            <b>9.1</b> The Services, the SaaS, the Mobile Application and all Intellectual Property
            Rights in them are and remain owned by the Provider and its licensors. Nothing in the
            Agreement grants the Customer any proprietary right in them except the limited licence
            expressly set out in Article 4.
          </Paragraph>
          <Paragraph>
            <b>9.2</b> Any derivative work of any part of the SaaS created by a Customer or its
            Authorized Users shall be owned solely by the Provider, and the Customer assigns to the
            Provider all right, title and interest it may acquire in any such derivative work.
          </Paragraph>
          <Paragraph>
            <b>9.3</b> The Customer grants the Provider a non-exclusive, non-transferable licence to
            use, host, copy, display and transmit its User Content as necessary to perform the
            Provider’s obligations and exercise its rights under the Agreement.
          </Paragraph>
          <Paragraph>
            <b>9.4</b> If the Provider receives any feedback from a Customer or its Authorized Users
            relating to the Services, it may use and exploit that feedback without restriction and
            without compensation.
          </Paragraph>
          <Paragraph>
            <b>9.5</b> All software, algorithms, artificial intelligence systems, recommendation
            engines, platform improvements, updates, enhancements and related Intellectual Property
            Rights developed or used by the Provider shall remain the exclusive property of the
            Provider.
          </Paragraph>
          <Paragraph>
            <b>9.6</b> The Provider reserves all rights not expressly granted under the Agreement.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '10. CONFIDENTIALITY AND PUBLICITY',
      description: (
        <Box>
          <Paragraph>
            <b>10.1</b> Each Party shall keep confidential all Confidential Information of the other
            Party and all activities of the Parties under the Agreement, and shall not use them
            except as necessary to perform the Agreement. The Provider is authorized to use a
            Customer’s Confidential Information for the purpose of providing the Services.
          </Paragraph>
          <Paragraph>
            <b>10.2</b> Confidential Information excludes information that is or becomes publicly
            available without fault of the recipient, and information whose disclosure is required
            by law or by a judicial or regulatory authority.
          </Paragraph>
          <Paragraph>
            <b>10.3</b> Neither Party may use the name of the other in published materials without
            prior written consent, save that the Provider may include a Customer’s name in its
            customer list and, with the Customer’s prior written approval, describe in general terms
            the nature of the Services provided.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '11. DATA PROTECTION',
      description: (
        <Box>
          <Paragraph>
            <b>11.1</b> Each Party shall comply with all Applicable Laws relating to the protection
            of personal data, including Lebanese Law No. 81 of 2018 on Electronic Transactions and
            Personal Data.
          </Paragraph>
          <Paragraph>
            <b>11.2</b> The Provider’s collection, use and disclosure of personal data in connection
            with the Services is governed by the Privacy Policy, which forms part of the Agreement.
          </Paragraph>
          <Paragraph>
            <b>11.3</b> Each Customer represents and warrants that, in relation to any personal data
            it submits, uploads, transmits or otherwise makes available through the Services, it has
            a lawful basis to do so and has complied with all applicable notice, transparency,
            consent, authorisation and other requirements imposed by Applicable Laws. Each Customer
            shall be solely responsible for the lawfulness, accuracy and legitimacy of such personal
            data and shall defend, indemnify and hold harmless the Provider against any claim, loss,
            liability, penalty, cost or expense arising from the Customer’s breach of this Article.
          </Paragraph>
          <Paragraph>
            <b>11.4</b> Where the Provider merely facilitates the transmission or exchange of
            personal data between a Supplier and an Operator, each such Customer shall be
            independently responsible for determining the purposes and means of its own processing
            of such personal data and for complying with its obligations under Applicable Laws. The
            Provider shall not be responsible for a Customer’s independent processing of personal
            data, including any processing carried out after such data has been made available to or
            received by that Customer, except to the extent such responsibility is mandatorily
            imposed on the Provider under Applicable Laws.
          </Paragraph>
          <Paragraph>
            <b>11.5</b> Each Customer shall defend, indemnify and hold harmless the Provider, its
            affiliates, Licensees, officers, directors, employees and representatives from and
            against any claims, investigations, proceedings, liabilities, fines, penalties, damages,
            costs and expenses, including reasonable legal fees, arising out of or relating to (a)
            the Customer’s unlawful processing of personal data; (b) the Customer’s failure to
            obtain any required consent, authorisation or lawful basis; (c) the Customer’s breach of
            Applicable Laws relating to personal data; or (d) any personal data submitted, uploaded,
            transmitted or otherwise made available by the Customer through the Services in breach
            of Applicable Laws or the Agreement.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '12. GENERAL',
      description: (
        <Box>
          <Paragraph>
            <b>12.1 Refunds and disputes.</b> Any dispute concerning a Product, including any claim
            for rejection or refund, is a matter solely between the relevant Operator and the
            relevant Supplier. Before settlement of a Purchase Order, an Operator may reject
            Products on submission of documented evidence of non-conformity. After settlement, an
            Operator dissatisfied with a Product shall submit documented evidence in support of a
            refund application through the SaaS; the Provider shall notify the Supplier, and if the
            Supplier approves the application it shall effect the refund directly to the Operator.
            The Provider acts solely as a facilitator of this process, disclaims all liability in
            relation to refunds, and the Supplier is solely responsible for the approval, rejection
            and payment of any refund.
          </Paragraph>
          <Paragraph>
            <b>12.2 Governing law.</b> The Agreement is governed by the laws of Lebanon, without
            regard to its conflict of laws principles.
          </Paragraph>
          <Paragraph>
            <b>12.3 Dispute resolution.</b> The courts of Beirut, Lebanon shall have exclusive
            jurisdiction over any dispute arising out of or in connection with the Agreement. Either
            Party may seek interim or provisional relief from any court of competent jurisdiction to
            protect its rights. Each Party waives any right to a trial by jury.
          </Paragraph>
          <Paragraph>
            <b>12.4 Force majeure.</b> Neither Party shall be liable for any delay or failure in
            performance, other than a payment obligation, caused by any event beyond its reasonable
            control and without its fault or negligence.
          </Paragraph>
          <Paragraph>
            <b>12.5 Assignment.</b> The Customer may not assign or transfer the Agreement, in whole
            or in part, without the Provider’s prior written consent. The Provider may assign the
            Agreement to any affiliate, to any Licensee, or in connection with a merger,
            reorganisation or sale of all or substantially all of its assets, on written notice to
            the Customer.
          </Paragraph>
          <Paragraph>
            <b>12.6 Notices.</b> All notices under the Agreement shall be in writing and sent by
            registered mail, reputable courier or hand delivery to the addresses set out in the
            Services Agreement, and in addition by email to the Parties’ designated contacts. Either
            Party may change its notice address by written notice to the other.
          </Paragraph>
          <Paragraph>
            <b>12.7 Severability.</b> If any provision of the Agreement is held unenforceable, the
            Parties shall replace it with an enforceable provision reflecting the original intent as
            nearly as possible, and the remaining provisions shall remain in full force and effect.
          </Paragraph>
          <Paragraph>
            <b>12.8 Waiver.</b> The failure of either Party to enforce any provision of the
            Agreement shall not be a waiver of that provision or of any subsequent breach.
          </Paragraph>
          <Paragraph>
            <b>12.9 Construction.</b> Headings are for reference only and have no substantive
            effect. No provision shall be construed against a Party by reason of that Party having
            drafted it.
          </Paragraph>
          <Paragraph>
            <b>12.10 Entire agreement.</b> The Agreement, comprising these Terms and Conditions, the
            Privacy Policy, the Services Agreements and any Annexes and Statements of Work,
            constitutes the entire agreement between the Parties in relation to its subject matter
            and supersedes all prior negotiations and agreements.
          </Paragraph>
          <Paragraph>
            <b>12.11 Amendments.</b> The Provider may amend these Terms and Conditions from time to
            time. No amendment that materially affects a Customer’s rights or obligations shall be
            effective against that Customer until thirty (30) days after the Provider has given the
            Customer written notice of the amendment. If the Customer does not accept the amendment,
            it may terminate the Agreement without penalty by written notice given within that
            thirty (30) day period. By way of exception, amendments required by law, regulation or
            security may take effect immediately upon notice. Amendments that do not materially
            affect the Customer’s rights or obligations take effect on publication.
          </Paragraph>
          <Paragraph>
            The Provider shall not be liable solely as a result of implementing any amendment,
            modification or update made in accordance with this Article, provided that the Provider
            has complied with the applicable notice requirements and the amendment does not result
            in any liability that cannot lawfully be excluded or limited under Applicable Law.
          </Paragraph>
          <Paragraph>
            <b>12.12 Order of precedence.</b> In case of contradiction between these Terms and
            Conditions and a Services Agreement, the Services Agreement prevails.
          </Paragraph>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Terms and Conditions
      </Typography>
      <Box marginBottom={4}>
        <Paragraph>
          These Terms and Conditions (the <b>“Terms and Conditions”</b>) govern access to and use of
          the CARTIFY PORTAL mobile application and the Services made available through it. They
          form part of the Agreement between the Provider and each Customer. By subscribing to and
          using the Services, the Customer accepts these Terms and Conditions in full and undertakes
          to procure the compliance of its Authorized Users and Authorized Customer Entities with
          them.
        </Paragraph>
      </Box>
      {termsData.map((item, i) => (
        <Box key={i} marginBottom={i < termsData.length - 1 ? 4 : 0}>
          <TermsSection {...item} />
        </Box>
      ))}
    </Box>
  );
};

export default TermsContent;
